import React, { useState } from 'react';
import Layout from '../../layout/Layout.js';
import Section, { SectionTitle } from '../../common/Section.js';
import Button from '../../common/Button.js';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';

const ContactWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactForm = styled.form`
  background-color: var(--white);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  
  h3 {
    color: var(--primary);
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--dark);
  }
  
  input, textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid var(--gray-light);
    border-radius: var(--border-radius-sm);
    font-family: inherit;
    font-size: 1rem;
    transition: var(--transition);
    
    &:focus {
      outline: none;
      border-color: var(--secondary);
      box-shadow: 0 0 0 3px rgba(var(--secondary-rgb), 0.2);
    }
  }
  
  textarea {
    min-height: 150px;
    resize: vertical;
  }
  
  .optional {
    font-size: 0.8rem;
    color: var(--gray);
    margin-left: 0.5rem;
    font-weight: normal;
  }
`;

const ContactInfo = styled.div`
  h3 {
    color: var(--primary);
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
  }
  
  p {
    color: var(--gray);
    margin-bottom: 2rem;
    line-height: 1.6;
  }
`;

const ContactInfoList = styled.ul`
  margin-top: 2rem;
  
  li {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    
    svg {
      color: var(--secondary);
      font-size: 1.25rem;
      margin-right: 1rem;
      margin-top: 0.25rem;
    }
    
    .info {
      h4 {
        color: var(--dark);
        margin-bottom: 0.25rem;
      }
      
      p {
        margin-bottom: 0;
      }
      
      a {
        color: var(--secondary);
        text-decoration: none;
        transition: var(--transition);
        
        &:hover {
          color: var(--primary);
        }
      }
    }
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: var(--border-radius-sm);
  margin-bottom: 1.5rem;
  
  &.success {
    background-color: rgba(46, 204, 113, 0.15);
    color: #27ae60;
    border: 1px solid rgba(46, 204, 113, 0.3);
  }
  
  &.error {
    background-color: rgba(231, 76, 60, 0.15);
    color: #e74c3c;
    border: 1px solid rgba(231, 76, 60, 0.3);
  }
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [status, setStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = (e) => {
    // Client-side validation for enhanced user experience
    // Form will still be submitted to server for server-side handling
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      e.preventDefault();
      setStatus({
        submitted: true,
        success: false,
        message: 'Please fill out all required fields.'
      });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      e.preventDefault();
      setStatus({
        submitted: true,
        success: false,
        message: 'Please enter a valid email address.'
      });
      return;
    }
    
    // Let the form submit normally to the PHP handler
    // The PHP script will handle sending the email and redirecting
  };
  
  return (
    <Layout>
      <Section>
        <div className="container">
          <SectionTitle>
            <h2>Contact Us</h2>
            <p>Get in touch with our team for inquiries and information</p>
          </SectionTitle>
          
          <ContactWrapper>
            <ContactForm action="/contact.php" method="POST">
              <h3>Send Us a Message</h3>
              
              {status.submitted && (
                <Alert className={status.success ? 'success' : 'error'}>
                  {status.message}
                </Alert>
              )}
              
              <FormGroup>
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="phone">
                  Phone Number <span className="optional">(optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </FormGroup>
              
              <FormGroup>
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </FormGroup>
              
              <Button type="submit" variant="secondary" fullWidth onClick={handleSubmit}>
                Submit
              </Button>
            </ContactForm>
            
            <ContactInfo>
              <h3>Get in Touch</h3>
              <p>
                Have questions about our homes, services, or just want to learn more? 
                Reach out to us directly using the contact form or through any of the methods below.
              </p>
              
              <ContactInfoList>
                <li>
                  <FaMapMarkerAlt />
                  <div className="info">
                    <h4>Location</h4>
                    <p>
                      <a href="https://www.google.com/maps/place/Greensboro,+NC" target="_blank" rel="noopener noreferrer">
                        Serving the NC Triad Area
                      </a>
                    </p>
                  </div>
                </li>
                
                <li>
                  <FaPhone />
                  <div className="info">
                    <h4>Phone</h4>
                    <p>
                      <a href="tel:3365166716">(336) 516-6716</a>
                    </p>
                  </div>
                </li>
                
                <li>
                  <FaEnvelope />
                  <div className="info">
                    <h4>Email</h4>
                    <p>
                      <a href="mailto:carl@shoffnerhomes.com?subject=Shoffner%20Homes%20Website%20Inquiry">
                        carl@shoffnerhomes.com
                      </a>
                    </p>
                  </div>
                </li>
              </ContactInfoList>
              
              <p style={{ marginTop: '2rem' }}>
                <strong>Business Hours:</strong><br />
                Monday - Friday: 9:00 AM - 5:00 PM<br />
                Saturday: By appointment<br />
                Sunday: Closed
              </p>
            </ContactInfo>
          </ContactWrapper>
        </div>
      </Section>
    </Layout>
  );
};

export default ContactPage;